import { MessageContent } from '@/types/msq'

// Constants
// Matches rosetta-ai's ImageMimeType union; browsers report JPEG files as image/jpeg.
export const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
export const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

// Types
export interface UploadedImage {
  mimeType: string
  base64Data: string
  preview: string
  fileName: string
}

// Validation function
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  if (!VALID_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type: ${file.name}. Only JPEG, PNG, GIF, and WebP images are allowed.`
    }
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large: ${file.name}. Maximum size is 5MB.`
    }
  }
  
  return { valid: true }
}

// Base64 conversion function
export function readImageAsBase64(file: File): Promise<UploadedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = () => {
      const result = reader.result as string
      const base64Data = result.split(',')[1]
      
      resolve({
        mimeType: file.type,
        base64Data: base64Data,
        preview: result,
        fileName: file.name
      })
    }
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`))
    }
    
    reader.readAsDataURL(file)
  })
}

// Create message content array from text and images
export function createMessageContent(text: string, images: UploadedImage[]): string | MessageContent[] {
  if (images.length === 0) {
    return text
  }
  
  const contentArray: MessageContent[] = []
  
  // Add image content first
  images.forEach(img => {
    contentArray.push({
      type: 'image',
      image: {
        mimeType: img.mimeType,
        base64Data: img.base64Data
      }
    })
  })
  
  // Add text content if present
  if (text.trim()) {
    contentArray.push({
      type: 'text',
      text: text
    })
  }
  
  return contentArray
}

// Process multiple files
export async function processImageFiles(files: File[]): Promise<{ 
  images: UploadedImage[]; 
  errors: string[] 
}> {
  const images: UploadedImage[] = []
  const errors: string[] = []
  
  for (const file of files) {
    const validation = validateImageFile(file)
    
    if (!validation.valid) {
      errors.push(validation.error!)
      continue
    }
    
    try {
      const imageData = await readImageAsBase64(file)
      images.push(imageData)
    } catch (error) {
      errors.push(`Failed to read image: ${file.name}`)
    }
  }
  
  return { images, errors }
}
